import React, { memo, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';

import logo from '../../assets/img/content/logo-new.webp';
import DropDown, { DropDownElement } from '../../components/helpers/DropDown';
import { handleLogout, loadProfile } from '../../actions/auth';
import { Avatar } from '../Avatar';
import { AppSwitcher } from '../AppSwitcher';
import config from '../../config';
import { checkAccess } from '../../utils/checkPermissions';
import { usePopupContext } from '../../contexts/PopupContext';

import style from './style.module.css';

const AppHeaderComponent = ({ children, actions, user, onLogout, onProfileSave }) => {
  const role = user.getIn(['roles', 0]);
  const roleSlug = role?.get('slug');
  const hasAccess = role ? checkAccess(role) : false;

  const navLinks = useMemo(
    () =>
      [
        hasAccess && { to: '/next-actions', label: 'Next Actions' },
        roleSlug &&
          ![config.RESEARCHER, config.PROCESS_MANAGER].includes(roleSlug) && {
            to: '/leads-report',
            label: 'Leads',
          },
        hasAccess && { to: '/backlog', label: 'Backlog' },
      ].filter(Boolean),
    [hasAccess, roleSlug],
  );

  const userName = `${user?.get('firstName')} ${user?.get('lastName')}`;
  const userTitle = user.get('title');

  const dropdownText =
    userTitle === '' ? (
      <b>{userName}&nbsp;</b>
    ) : (
      <div className={style.dropdownText}>
        <b>{userName}</b>
        <div className={style.role}>{userTitle}</div>
      </div>
    );

  const { openPopup, closePopup } = usePopupContext();

  const handleConfirmLogout = useCallback(() => {
    closePopup();
    onLogout();
  }, [closePopup, onLogout]);

  const handleMenuChange = useCallback(
    event => {
      event.preventDefault();

      if (event.target.value === 'profile') {
        openPopup('ProfilePopup', { userId: user.get('id'), onSave: onProfileSave });
      }

      if (event.target.value === 'logout') {
        openPopup('ConfirmPopup', {
          header: 'Log Out',
          message: 'Do you really want to log out?',
          yes: 'Log Out',
          onOk: handleConfirmLogout,
          onCancel: closePopup,
        });
      }
    },
    [openPopup, closePopup, handleConfirmLogout, user, onProfileSave],
  );

  return (
    <header className={style.host}>
      <NavLink to="/">
        <img alt="logo" className={style.logo} src={logo} />
      </NavLink>
      <div className={style.children}>{children}</div>
      <AppSwitcher />
      <div className={style.rightPart}>
        {actions && <div className={style.actions}>{actions}</div>}
        <div className={style.userName}>
          <DropDown
            align="right"
            buttonContent={
              <div className={style.dropdownContent}>
                {dropdownText}
                <div className="ml4 mr4">
                  <Avatar name={userName} size="middle" />
                </div>
              </div>
            }
            className={style.buttonMenu}
            id="userMenuDropDown"
            name="userMenu"
            onChange={handleMenuChange}
            value=""
            wrapperClassName={style.menuWrapper}
          >
            {navLinks.map(link => (
              <DropDownElement key={link.to} target="_blank" to={link.to} type="link">
                {link.label}
              </DropDownElement>
            ))}
            {hasAccess && (
              <DropDownElement target="_blank" to="/tag" type="link">
                Tag
              </DropDownElement>
            )}
            {hasAccess && (
              <DropDownElement target="_blank" to="/windward" type="link">
                Windward
              </DropDownElement>
            )}
            {roleSlug === config.ADMINISTRATOR && (
              <DropDownElement target="_blank" to="/user" type="link">
                Users
              </DropDownElement>
            )}
            <DropDownElement className="text-decoration-none" value="profile">
              Profile
            </DropDownElement>
            <DropDownElement className="text-decoration-none" value="logout">
              Log Off
            </DropDownElement>
          </DropDown>
        </div>
      </div>
    </header>
  );
};

function mapDispatchToProps(dispatch) {
  return {
    onLogout: () => dispatch(handleLogout()),
    onProfileSave: () => dispatch(loadProfile()),
  };
}

const mapStateToProps = state => ({
  user: state.auth.get('user', null),
});

export const AppHeader = connect(mapStateToProps, mapDispatchToProps)(memo(AppHeaderComponent));
